import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { SparklesIcon, ArrowRightIcon } from 'lucide-react';
export function HeroSection() {
  const containerRef = useRef<HTMLElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: containerRef,
    offset: ['start start', 'end start']
  });
  const y = useTransform(scrollYProgress, [0, 1], [0, 150]);
  const orbY1 = useTransform(scrollYProgress, [0, 1], [0, 100]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], [0, 200]);
  const orbY3 = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const opacity = useTransform(scrollYProgress, [0, 0.8], [1, 0]);
  return <section ref={containerRef} className="relative min-h-screen flex items-center justify-center overflow-hidden px-4 py-20" aria-label="Hero section" data-id="element-46">
      {/* Background Gradient */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0f] via-[#0f0f1a] to-[#0a0a0f]" aria-hidden="true" data-id="element-47" />

      {/* Floating Orbs */}
      <motion.div style={{
      y: orbY1
    }} className="orb orb-blue w-[400px] h-[400px] -top-20 -left-40" aria-hidden="true" data-id="element-48" />
      <motion.div style={{
      y: orbY2
    }} className="orb orb-purple w-[500px] h-[500px] top-1/3 -right-60" aria-hidden="true" data-id="element-49" />
      <motion.div style={{
      y: orbY3
    }} className="orb orb-cyan w-[300px] h-[300px] bottom-20 left-1/4" aria-hidden="true" data-id="element-50" />

      {/* Content */}
      <motion.div style={{
      y,
      opacity
    }} className="relative z-10 w-full max-w-5xl mx-auto" data-id="element-51">
        {/* Main Holographic Card */}
        <motion.div initial={{
        opacity: 0,
        y: 40,
        filter: 'blur(20px)'
      }} animate={{
        opacity: 1,
        y: 0,
        filter: 'blur(0px)'
      }} transition={{
        duration: 0.8,
        ease: [0.16, 1, 0.3, 1]
      }} className="gradient-border gradient-border-thick rounded-3xl p-1" data-id="element-52">
          <div className="glass rounded-3xl p-8 md:p-16 text-center relative overflow-hidden" data-id="element-53">
            {/* Inner Refraction */}
            <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 via-transparent to-purple-500/5 pointer-events-none" aria-hidden="true" data-id="element-54" />

            {/* Badge */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.8
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            delay: 0.3,
            duration: 0.5
          }} className="inline-flex items-center gap-2 px-4 py-2 rounded-full glass mb-8" data-id="element-55">
              <SparklesIcon className="w-4 h-4 text-cyan-400" data-id="element-56" />
              <span className="text-sm text-gray-300" data-id="element-57">
                Next-Generation Interface
              </span>
            </motion.div>

            {/* Heading */}
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.4,
            duration: 0.6
          }} className="text-4xl md:text-6xl lg:text-7xl font-bold mb-6 leading-tight" data-id="element-58">
              <span className="text-holo-animated" data-id="element-59">Holographic</span>
              <br data-id="element-60" />
              <span className="text-white" data-id="element-61">Design System</span>
            </motion.h1>

            {/* Subheading */}
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.5,
            duration: 0.6
          }} className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto mb-10 leading-relaxed" data-id="element-62">
              Experience the future of digital interfaces with iridescent
              gradients, glass morphism, and immersive 3D interactions that feel
              alive.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.6,
            duration: 0.6
          }} className="flex flex-col sm:flex-row items-center justify-center gap-4" data-id="element-63">
              <button className="btn-glass btn-glass-primary flex items-center gap-2 group" data-id="element-64">
                <span data-id="element-65">Get Started</span>
                <ArrowRightIcon className="w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-66" />
              </button>
              <button className="btn-glass" data-id="element-67">View Documentation</button>
            </motion.div>

            {/* Stats Row */}
            <motion.div initial={{
            opacity: 0
          }} animate={{
            opacity: 1
          }} transition={{
            delay: 0.8,
            duration: 0.6
          }} className="mt-16 pt-8 border-t border-white/5 grid grid-cols-3 gap-8" data-id="element-68">
              {[{
              value: '10K+',
              label: 'Active Users'
            }, {
              value: '99.9%',
              label: 'Uptime'
            }, {
              value: '50ms',
              label: 'Response Time'
            }].map((stat, index) => <div key={index} className="text-center" data-id="element-69">
                  <div className="text-2xl md:text-3xl font-bold text-holo" data-id="element-70">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-500 mt-1" data-id="element-71">{stat.label}</div>
                </div>)}
            </motion.div>
          </div>
        </motion.div>

        {/* Floating Elements */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1,
        duration: 1
      }} className="absolute -bottom-10 left-1/2 -translate-x-1/2" aria-hidden="true" data-id="element-72">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity,
          ease: 'easeInOut'
        }} className="w-6 h-10 rounded-full border-2 border-white/20 flex items-start justify-center p-2" data-id="element-73">
            <motion.div animate={{
            y: [0, 8, 0],
            opacity: [1, 0.3, 1]
          }} transition={{
            duration: 2,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="w-1 h-2 bg-white/50 rounded-full" data-id="element-74" />
          </motion.div>
        </motion.div>
      </motion.div>
    </section>;
}